import React, { useCallback } from 'react';

import { HeaderActionType, HeaderActionPatternType } from 'config/types';
import * as consts from 'config/consts';

function createCtx<T>(): readonly [() => T, React.Provider<T | undefined>] {
  const ctx = React.createContext<T | undefined>(undefined);
  const useCtx = (): T => {
    const c = React.useContext(ctx);
    if (!c) throw new Error('useCtx must be inside a Provider with a value');
    return c;
  };

  return [useCtx, ctx.Provider] as const;
}

type CustomizeHeaderContextType = {
  customTitle: string;
  setCustomTitle: React.Dispatch<React.SetStateAction<string>>;
  action: (type: HeaderActionType) => void;
  setAction: React.Dispatch<
    React.SetStateAction<(type: HeaderActionType) => void>
  >;
  actionPattern: HeaderActionPatternType;
  setActionPattern: React.Dispatch<
    React.SetStateAction<HeaderActionPatternType>
  >;
  setBackFunction: (func?: () => void) => { cleanup: () => void };
  backFunction?: () => void;
  setBack: (value: { to: string } | { backFunction: () => void }) => void;
  popBack: () => { to: string } | { func: () => void } | null;
  withBell: boolean;
  // このsetWithBellは、コンポーネントが代わったタイミングではオフにならなければいけない。だから、useEffectにそのまま入れる用途のみに制限するように公開してみた
  // 使い方:
  // useEffect(setWithBell, []);
  setWithBell: (withBell?: boolean) => () => void;
  withNavigation: boolean;
  setWithNavigation: (withNavigation?: boolean) => () => void;
  withLogo: boolean;
  setWithLogo: (withLogo?: boolean) => () => void;
  withBackIconWord: boolean;
  setWithBackIconWord: (withBackIconWord?: boolean) => () => void;
  accountOrAuthenticationError: boolean;
  setAccountOrAuthenticationError: (
    accountOrAuthenticationError?: boolean
  ) => () => void;
};

export const [useCustomizeHeaderContext, Provider] = createCtx<
  CustomizeHeaderContextType
>();

const useCustomizeHeader = (): CustomizeHeaderContextType => {
  const [action, setAction] = React.useState<(type: HeaderActionType) => void>(
    () => (type: HeaderActionType): void => {
      return;
    }
  );
  const [customTitle, setCustomTitle] = React.useState<string>('');
  const [actionPattern, setActionPattern] = React.useState<
    HeaderActionPatternType
  >(consts.HeaderActionPatternType.なし);
  const [back, setBack_] = React.useState<
    { to: string } | { func: () => void } | undefined
  >();
  const popBack = (): { to: string } | { func: () => void } | null => {
    const ret = back;
    if (!ret) return null;
    setBack_(undefined);
    return ret;
  };
  const setBack = React.useCallback(
    (value: { to: string } | { backFunction: () => void }): void => {
      if ('to' in value) setBack_(value);
      if ('backFunction' in value) setBack_({ func: value.backFunction });
    },
    []
  );
  const [withBell, setWithBell] = React.useState<boolean>(false);
  const [withBackIconWord, setWithBackIconWord] = React.useState<boolean>(
    false
  );
  const [withNavigation, setWithNavigation] = React.useState<boolean>(false);
  const [withLogo, setWithLogo] = React.useState<boolean>(false);
  const setComponentSpecificFlag = useCallback(
    (setter: React.Dispatch<React.SetStateAction<boolean>>) => (
      flag?: boolean
    ) => {
      setter(flag ?? true);
      return () => setter(false);
    },
    []
  );
  const [backFunction, setBackFunction_] = React.useState<
    { func: () => void } | undefined
  >();
  const setBackFunction = useCallback((func?: () => void) => {
    setBackFunction_(func ? { func } : undefined);
    return { cleanup: () => setBackFunction_(undefined) };
  }, []);
  const [
    accountOrAuthenticationError,
    setAccountOrAuthenticationError,
  ] = React.useState<boolean>(false);

  return {
    customTitle,
    setCustomTitle,
    action,
    setAction,
    actionPattern,
    setActionPattern,
    setBack,
    popBack,
    withBell,
    setWithBell: setComponentSpecificFlag(setWithBell),
    withNavigation,
    setWithNavigation: setComponentSpecificFlag(setWithNavigation),
    withLogo,
    setWithLogo: setComponentSpecificFlag(setWithLogo),
    withBackIconWord,
    setWithBackIconWord: setComponentSpecificFlag(setWithBackIconWord),
    backFunction: backFunction?.func,
    setBackFunction,
    accountOrAuthenticationError,
    setAccountOrAuthenticationError: setComponentSpecificFlag(
      setAccountOrAuthenticationError
    ),
  };
};

export const CustomizeHeaderProvider: React.FC = props => {
  const customizeHeader = useCustomizeHeader();
  return <Provider value={customizeHeader}>{props.children}</Provider>;
};
